import React, { createContext, useContext, useState, useEffect, ReactNode, Component, ErrorInfo } from 'react';
import { UserResponse, TokenResponse } from '../types';
import { authService, handleSessionExpiry } from '../services/api';
import { tokenIdentityProvider } from '../utils/authToken';

interface AuthContextType {
  user: UserResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Which login path produced the current token, e.g. "linkedin" (from the JWT `identity_provider` claim). */
  loginProvider: string | null;
  login: (token: TokenResponse) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('React Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message 
        ? String(this.state.error.message) 
        : 'An error occurred';
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h1>Something went wrong</h1>
          <p>{errorMessage}</p>
          <button onClick={() => window.location.reload()}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginProvider, setLoginProvider] = useState<string | null>(null);
  const [hasToken, setHasToken] = useState(!!localStorage.getItem('access_token'));

  const readLoginProvider = (): string | null => tokenIdentityProvider(localStorage.getItem('access_token'));

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 < Date.now()) {
          handleSessionExpiry();
          return;
        }
      } catch {
        handleSessionExpiry();
        return;
      }
      setLoginProvider(readLoginProvider());
      refreshUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const clearAuthState = () => {
      setUser(null);
      setHasToken(false);
    };

    const handleStorage = (e: StorageEvent) => {
      if ((e.key === 'access_token' || e.key === 'refresh_token') && !e.newValue) {
        clearAuthState();
      }
    };

    const handleSessionRecheck = () => {
      if (document.visibilityState !== 'visible' && !document.hasFocus()) return;
      const token = localStorage.getItem('access_token');
      if (!token) {
        clearAuthState();
        return;
      }
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 < Date.now()) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          clearAuthState();
        }
      } catch {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        clearAuthState();
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleSessionRecheck);
    document.addEventListener('visibilitychange', handleSessionRecheck);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleSessionRecheck);
      document.removeEventListener('visibilitychange', handleSessionRecheck);
    };
  }, []);

  const refreshUser = async () => {
    try {
      const response = await authService.getProfile();
      setUser(response.data.data);
    } catch (error) {
      console.error('AuthContext: Failed to fetch user:', error);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setHasToken(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (token: TokenResponse) => {
    localStorage.setItem('access_token', token.access_token);
    localStorage.setItem('refresh_token', token.refresh_token);
    setHasToken(true);
    setLoginProvider(readLoginProvider());
    await refreshUser();
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setHasToken(false);
      setLoginProvider(null);
    }
  };

  return (
    <ErrorBoundary>
      <AuthContext.Provider
        value={{
          user,
          isAuthenticated: !!user || hasToken,
          isLoading,
          loginProvider,
          login,
          logout,
          refreshUser,
        }}
      >
        {children}
      </AuthContext.Provider>
    </ErrorBoundary>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
